'use client';

import { BuilderElement, BuilderStore } from '@builder.io/react';
import * as blotout from 'components/analytics/blotout';
import * as gtm from 'components/analytics/gtm';
import { addItem } from 'components/cart/actions';
import useCart from 'components/hooks/use-cart';
import ButtonArrow from 'components/icons/button-arrow';
import LoadingDots from 'components/loading-dots';
import { useStorefrontDispatch } from 'components/providers/storefront-provider';
import { getCookie } from 'cookies-next';
import { Product, ProductVariant } from 'lib/bigcommerce/types';
import { CSSProperties, FormEvent, useEffect, useState, useTransition } from 'react';
import * as Intellisuggest from '../ss-analytics/intellisuggest';
import Modal from './modal';

export function SubmitButton({
  availableForSale,
  isInStock,
  selectedQuantity,
  selectedVariantId,
  buttonColor,
  buttonSize,
  buttonEmbellish,
  buttonText,
  buttonFontSize,
  notifyMe,
  pending,
  productHandle
}: {
  availableForSale: boolean;
  isInStock: boolean;
  selectedQuantity?: number;
  selectedVariantId: string | undefined;
  buttonColor: string;
  buttonSize?: string;
  buttonEmbellish?: string;
  buttonText: string;
  buttonFontSize?: number;
  notifyMe: boolean;
  pending: boolean;
  productHandle: string;
}) {
  let buttonStyle = {};
  if (buttonFontSize && buttonFontSize > 0) {
    buttonStyle = {
      'fontSize': `${buttonFontSize}px`
    }
  }

  if (!selectedVariantId) {
    return (
      <a
        aria-label="Please select an option"
        aria-disabled
        className={getClassName(buttonColor, buttonSize || 'medium', buttonEmbellish || 'none')}
        href={productHandle}
        style={buttonStyle}
      >
        View Options
      </a>
    );
  }

  const isAvailable = availableForSale && isInStock;
  const showNotifyMe = availableForSale && notifyMe === true && !isInStock;
  const showOutOfStock = !availableForSale || (notifyMe === false && !isInStock)

  return (
    <>
      {showNotifyMe && <NotifyMeModal buttonFontSize={buttonFontSize} />}
      {showOutOfStock && <OutOfStockButton buttonStyle={buttonStyle} />}
      {isAvailable && <AddToCartButton
        buttonColor={buttonColor}
        buttonSize={buttonSize}
        buttonEmbellish={buttonEmbellish}
        buttonStyle={buttonStyle}
        buttonText={buttonText}
        pending={pending}
      />}
    </>
  );
}

function AddToCartButton({
  buttonColor,
  buttonSize,
  buttonEmbellish,
  buttonStyle,
  buttonText = "Add to cart",
  pending
}: {
  buttonColor: string;
  buttonSize?: string;
  buttonEmbellish?: string;
  buttonStyle: CSSProperties | undefined;
  buttonText?: string;
  pending: boolean;
}) {
    let embelishStyle = '';
    if(buttonColor.match(/(^4P.+)/))
        embelishStyle = `cta-4p-button-embellish-${buttonColor.replace(/(^4P)/,'').replace(/(\s)/g,'').toLowerCase()}`;
  return (
    <>
        { embelishStyle && buttonEmbellish === 'Circle' && (
            <img src="https://cdn.builder.io/api/v1/image/assets%2F1adbd5971dac4273891f273603729777%2Fab43a3fc60de4a7aa41dde3b4c17d3ac" className={`cta-4p-button-embellish cta-4p-button-embellish-circle ${embelishStyle}`} alt=""/>
        )}
        { embelishStyle && buttonEmbellish === 'Arrows' && (
            <span className={`cta-4p-button-embellish cta-4p-button-embellish-arrow cta-4p-button-embellish-arrow-left ${embelishStyle}`}>
                <ButtonArrow />
            </span>
        )}
        <button
        aria-label="Add to cart"
        className={getClassName(buttonColor, buttonSize || 'medium', buttonEmbellish || 'none')}
        style={buttonStyle}
        onClick={(e: React.FormEvent<HTMLButtonElement>) => {
            if (pending) e.preventDefault();
        }}
        >
        {buttonText}
        {pending && <LoadingDots className="loading-dots-dark" />}
        </button>
        { embelishStyle && buttonEmbellish === 'Arrows' && (
            <span className={`cta-4p-button-embellish cta-4p-button-embellish-arrow cta-4p-button-embellish-arrow-right ${embelishStyle}`}>
                <ButtonArrow />
            </span>
        )}
    </>
  );
}

function getClassName(buttonColor: string, buttonSize: string, buttonEmbellish: string) {
    let isFancy = false;
    let buttonClass = '';
    switch (buttonColor){
        case 'Primary':
            buttonClass = 'cta-button cta-button-atc';
            break;
        case 'Primary Inverted':
            buttonClass = 'cta-button cta-button-inverted';
            break;
        case 'Secondary':
            buttonClass = 'cta-button cta-button-secondary';
            break;
        case 'Secondary Inverted':
            buttonClass = 'cta-button cta-button-secondary-inverted';
            break;
        case 'ATC Tile':
            buttonClass = 'tile__atc';
            break;
        case '4P Orange 1':
            buttonClass = 'cta-button cta-4p-button cta-4p-button-orange1';
            isFancy = true;
            break;
        case '4P Orange 2':
            buttonClass = 'cta-button cta-4p-button cta-4p-button-orange2';
            isFancy = true;
            break;
        case '4P Green':
            buttonClass = 'cta-button cta-4p-button cta-4p-button-green';
            isFancy = true;
            break;
    }
    if(isFancy){
        buttonClass += ` cta-4p-button-${buttonSize.toLowerCase()}`;
        if(buttonEmbellish !== 'None') 
            buttonClass += ` cta-4p-button-embellish-${buttonEmbellish.toLowerCase()}`;
    }

    return buttonClass;
}

function OutOfStockButton({
  buttonStyle,
}: {
  buttonStyle: CSSProperties | undefined;
}) {
  return (
    <button
      aria-disabled
      className="cta-button cta-button-atc"
      disabled
      style={buttonStyle}
    >
      Out Of Stock
    </button>
  );
}

function NotifyMeModal({
  buttonFontSize
}: {
  buttonFontSize: number | undefined;
}) {
  return (
    <Modal
      buttonClasses="cta-button cta-button-atc"
      buttonFontSize={buttonFontSize}
      buttonText="Notify Me"
      preventDefault={true}
    >
      Placement for Klaviyo content
    </Modal>
  );
}

export function AddToCartCTA({
  builderState,
  apiProduct,
  setQuantity,
  variantSku,
  buttonColor,
  buttonSize,
  buttonEmbellish,
  buttonText,
  buttonFontSize
}: {
  builderState?: BuilderStore;
  builderBlock?: BuilderElement;
  apiProduct: Product;
  setQuantity: number;
  variantSku: string;
  buttonColor: string;
  buttonSize?: string;
  buttonEmbellish?: string;
  buttonText: string;
  buttonFontSize: number | undefined;
}) {
  const { mutate } = useCart();
  const [message, setMessage] = useState<undefined | string>(undefined);
  const [isPending, startTransition] = useTransition();
  const dispatch = useStorefrontDispatch();

  if (!apiProduct && builderState?.state?.apiProduct) {
    apiProduct = builderState.state.apiProduct;
  }

  useEffect(() => {
    if(apiProduct){
      gtm.viewItemList({
        product: apiProduct,
        itemListId: 'related_products',
        itemListName: 'Related Products'
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!apiProduct) {
    return <></>;
  }
  const defaultVariantId = apiProduct.variants.length === 1 ? apiProduct.variants[0]?.id : undefined;
  const defaultProductId = apiProduct.variants.length === 1 ? apiProduct.variants[0]?.parentId : undefined;
  const variant = apiProduct.variants.find((variant: ProductVariant) =>
    variant.sku === variantSku
  );
  const selectedVariantId = variant?.id || defaultVariantId;
  const selectedProductId = variant?.parentId || defaultProductId;
  const selectedQuantity = setQuantity;
  const inStock = variant?.inventory.isInStock || false;

  // Set product-level notifyMe boolean
  let notifyMe = false;
  apiProduct && apiProduct.productMetafields.forEach((field: any) => {
    field.key === 'notify-me' && field.value === 'true' ? notifyMe = true : null;
    return;
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    startTransition(async () => {
      setMessage(undefined);
      const result = await addItem(null, { selectedProductId, selectedVariantId, selectedQuantity });
      if (result.isSuccess) {
        setMessage('Success')
        if (result.value) {
          const item = result.value.lines.find(l => l.merchandise.id === selectedVariantId);
          const siteId = process.env.NEXT_PUBLIC_SS_INDEX ?? '';
          const shopperId = getCookie('ssShopperId')?.toString() ?? '';
          const cart = result.value;
          if (item) {
            gtm.addToCart({
              cart: cart,
              lineItem: item,
              quantity: selectedQuantity,
              itemListId: 'related_products',
              itemListName: 'Related Products'
            });
            blotout.addToCart({
              cart: cart,
              lineItem: item,
              quantity: selectedQuantity
            });
            Intellisuggest.BasketItems(siteId, shopperId, cart);

            // PushOwl
            const pushOwlCartId = cart.id;
            const pushOwlCartItems = cart.lines;
            const pushOwlItems: any = [];

            pushOwlCartItems.map((item: any) => {
              const pushOwlItem = {
                variantId: item.merchandise.id, 
                productId: item.merchandise.product.id, 
                quantity: item.quantity
              };
              pushOwlItems.push(pushOwlItem);
            });

            const pushOwlCart = {
              token: pushOwlCartId,
              items: pushOwlItems
            };
            window.pushowl.trigger('syncCart', pushOwlCart);
          }
        }
        mutate(result);
        dispatch({ type: 'OPEN_CART_CONFIRM', currentVariant: selectedVariantId });
      }
      else {
        console.log(result.error)
        setMessage(result.error)
      }
    });
  };

  return (
    <div className="add-to-cart-box">
      <form onSubmit={onSubmit}>
        <SubmitButton
          availableForSale={true}
          isInStock={inStock}
          selectedVariantId={selectedVariantId}
          buttonColor={buttonColor}
          buttonSize={buttonSize}
          buttonEmbellish={buttonEmbellish}
          buttonText={buttonText}
          buttonFontSize={buttonFontSize}
          notifyMe={notifyMe}
          pending={isPending}
          productHandle={apiProduct.handle}
        />
        <p aria-live="polite" className="sr-only" role="status">
          {message}
        </p>
      </form>
    </div>
  );
}
